import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, notFoundIdHandler, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  ImagesProspectCliente,
  ImagesProspectClientePagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchImagesProspectClientes = (
  params?: GetImagesProspectClientesParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['imagesProspectClientes-red', ...Object.values(params ?? {})],
    queryFn: () => getImagesProspectClientes(params),
    enabled: enabled,
  });
};

export const useGetImagesProspectCliente = (id: string) => {
  return useQuery({
    queryKey: ['imagesProspectCliente-red', id],
    queryFn: () => getImagesProspectCliente(id),
  });
};

export const useCreateImagesProspectCliente = ({
  navigate,
  returnUrl,
  returnErrorUrl,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createImagesProspectCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['imagesProspectClientes-red'],
      });
      navigate && returnUrl && navigate(returnUrl);
    },
    onError: () => {
      navigate && returnErrorUrl && navigate(returnErrorUrl);
    },
  });
};

export const useUpdateImagesProspectCliente = ({
  navigate,
  returnUrl,
  returnErrorUrl,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateImagesProspectCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['imagesProspectClientes-red'],
      });
      navigate && returnUrl && navigate(returnUrl);
    },
    onError: () => {
      navigate && returnErrorUrl && navigate(returnErrorUrl);
    },
  });
};

export const useDeleteImagesProspectCliente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteImagesProspectCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['imagesProspectClientes-red'],
      });
    },
    onError: () => {},
  });
};

///* axios
export type GetImagesProspectClientesParams = Partial<ImagesProspectCliente> & {
  page?: number;
  page_size?: number;
};

export type CreateImagesProspectClienteParams = ImagesProspectCliente;
export type UpdateImagesProspectClienteParams = {
  id: string;
  data: CreateImagesProspectClienteParams;
};

export const getImagesProspectClientes = async (
  params?: GetImagesProspectClientesParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return get<ImagesProspectClientePagingRes>(
    `${urlBackend}api/v1/imagenes/?${queryParams}`,
    true
  );
};

export const getImagesProspectCliente = async (id: string) => {
  const res = await get<ImagesProspectCliente>(
    `${urlBackend}api/v1/imagenes/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createImagesProspectCliente = async (
  data: CreateImagesProspectClienteParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<ImagesProspectCliente>(
    `${urlBackend}api/v1/imagenes/`,
    data,
    true
  );

  if (res.code !== 200 && res.code !== 201) {
    setAlert(true, res.message, res.status);
  }

  return res;
};

export const updateImagesProspectCliente = async ({
  id,
  data,
}: UpdateImagesProspectClienteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<ImagesProspectCliente>(
    `${urlBackend}api/v1/imagenes/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteImagesProspectCliente = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ImagesProspectCliente>(
    `${urlBackend}api/v1/imagenes/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
